/**
 * FIX-D337 (D-337 §2, §6) - the documents process as pure data: the state of a review document, its tone,
 * the tracking row (תומלל ← טיוטה ← אצל מנהל OTE ← נשלח ← נחתם) and the canvas "is it empty" test.
 */
import type { Tone } from '../components/ui/tone';

export type ReviewState = 'draft' | 'with_ote' | 'returned' | 'sent' | 'signed' | 'void';

export const REVIEW_TONE: Record<ReviewState, Tone> = {
  draft: 'neutral',
  with_ote: 'warning',
  returned: 'danger',
  sent: 'info',
  signed: 'success',
  void: 'neutral',
};

export function reviewTone(state: string | null | undefined): Tone {
  return (REVIEW_TONE as Record<string, Tone>)[state ?? ''] ?? 'neutral';
}

export type TrackStepKey = 'transcribed' | 'draft' | 'with_ote' | 'sent' | 'signed';
export interface TrackStep { key: TrackStepKey; done: boolean; current: boolean }

const ORDER: Record<string, number> = { draft: 1, returned: 1, with_ote: 2, sent: 3, signed: 4 };

/**
 * The tracking row of one document. `transcribed` is shown only when the document came from a meeting recording.
 * A returned document stands on "טיוטה" (the note is shown next to it).
 */
export function trackSteps(state: string, opts: { transcribed?: boolean | null } = {}): TrackStep[] {
  const keys: TrackStepKey[] = ['draft', 'with_ote', 'sent', 'signed'];
  const at = ORDER[state] ?? 0;
  const steps: TrackStep[] = keys.map((key, i) => {
    const n = i + 1;
    return { key, done: n < at || (state === 'signed' && n === at), current: n === at && state !== 'signed' };
  });
  if (opts.transcribed !== undefined && opts.transcribed !== null) {
    steps.unshift({ key: 'transcribed', done: !!opts.transcribed, current: false });
  }
  return steps;
}

/** RGBA pixels of a canvas: true when nothing was drawn (every pixel transparent or near white). */
export function isBlankCanvas(rgba: ArrayLike<number>, minInk = 30): boolean {
  let ink = 0;
  for (let i = 0; i < rgba.length; i += 4) {
    if (rgba[i + 3] < 40) continue;
    if (rgba[i] > 235 && rgba[i + 1] > 235 && rgba[i + 2] > 235) continue;
    ink += 1;
    if (ink >= minInk) return false;
  }
  return true;
}

/** The OTE manager's tabs on "מסמכים לחתימה". */
export const REVIEW_TABS = ['with_ote', 'returned', 'sent', 'signed'] as const;
export type ReviewTab = typeof REVIEW_TABS[number];

/** D-337 §2 (same rule as the server): a lead's quote, a characterization / development quote go through the OTE manager. */
export function quoteNeedsOteReview(q: { lead_id?: string | null; quote_type?: string | null } | null | undefined): boolean {
  return !!q && (!!q.lead_id || q.quote_type === 'characterization' || q.quote_type === 'development');
}

/**
 * FIX-D337-2 §6 (QA-D337 #11, D-337 §6 "מסך חדש לראש הצוות"): "חלוקות לאישור" in the menu only for the OTE manager,
 * a team lead of a project (the split goes to the package's team lead, who leads the project), or whoever the
 * permissions screen opened it for. Everyone else would see an empty page.
 */
export function seesTaskSplits(c: { isGeneralManager?: boolean; teamLeadProjectIds?: string[] } | null | undefined, openedByOverride = false): boolean {
  return openedByOverride || !!c?.isGeneralManager || (c?.teamLeadProjectIds?.length ?? 0) > 0;
}

/**
 * FIX-D337-2 §1: can this browser show a PDF inside an <iframe>? `navigator.pdfViewerEnabled` is false on
 * Android Chrome and other browsers without an inline viewer (there the iframe stays empty) - then only the
 * always-visible "פתיחת המסמך" / "הורדה" buttons are shown. Unknown (old browsers) = try the frame.
 */
export function canShowPdfInline(nav: { pdfViewerEnabled?: boolean } | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  return nav?.pdfViewerEnabled !== false;
}
